/**
 * Geography API Types
 *
 * Types for geographic hierarchies and tree structures.
 */

/**
 * Geographic Tree Node
 *
 * Represents a node in the geographic hierarchy tree.
 * This is a recursive structure where each node can have child nodes.
 */
export interface GeographicTreeNode {
  id: string;
  resolution: string; // e.g., "Organization", "Operating Company", "District"
  name: string;
  slug: string;
  selectable: boolean;
  customers: number | null;
  trees: GeographicTreeNode[]; // Recursive children
}

/**
 * Geographic Hierarchy
 *
 * Represents a complete geographic hierarchy for an organization.
 * Contains metadata and the root tree structure.
 */
export interface GeographicHierarchyItem {
  hierarchy_id: string;
  hierarchy_name: string;
  hierarchy_slug: string;
  hierarchy_description: string;
  hierarchy_display: boolean;
  organization_id: string;
  hierarchy_created_at: string;
  hierarchy_updated_at: string;
  trees: GeographicTreeNode[];
  child_count: number;
}

/**
 * API Response for geographic hierarchies
 */
export interface GeographicHierarchiesResponse {
  items: GeographicHierarchyItem[];
  count: number;
}

// ============================================================================
// Helper Types for working with the tree structure
// ============================================================================

/**
 * Flattened tree node with parent reference
 * Useful for dropdowns and selections
 */
export interface FlattenedGeographicNode {
  id: string;
  name: string;
  slug: string;
  resolution: string;
  selectable: boolean;
  customers: number | null;
  depth: number;
  parentId: string | null;
  path: string[]; // Array of ancestor names for breadcrumb
}

/**
 * Resolution levels in the hierarchy
 */
export type GeographicResolution =
  | "Organization"
  | "Operating Company"
  | "District"
  | string; // Allow for custom resolutions

// ============================================================================
// Utility functions for working with the tree
// ============================================================================

/**
 * Flatten the tree structure into a list
 * Useful for populating dropdowns or tables
 */
export function flattenGeographicTree(
  nodes: GeographicTreeNode[],
  parentId: string | null = null,
  depth: number = 0,
  path: string[] = [],
): FlattenedGeographicNode[] {
  const result: FlattenedGeographicNode[] = [];

  for (const node of nodes) {
    const currentPath = [...path, node.name];

    result.push({
      id: node.id,
      name: node.name,
      slug: node.slug,
      resolution: node.resolution,
      selectable: node.selectable,
      customers: node.customers,
      depth,
      parentId,
      path: currentPath,
    });

    // Recursively flatten children
    if (node.trees && node.trees.length > 0) {
      result.push(
        ...flattenGeographicTree(node.trees, node.id, depth + 1, currentPath),
      );
    }
  }

  return result;
}

/**
 * Find a node by ID in the tree
 */
export function findNodeById(
  nodes: GeographicTreeNode[],
  id: string,
): GeographicTreeNode | null {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }
    if (node.trees && node.trees.length > 0) {
      const found = findNodeById(node.trees, id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Get all nodes at a specific resolution level
 */
export function getNodesByResolution(
  nodes: GeographicTreeNode[],
  resolution: GeographicResolution,
): GeographicTreeNode[] {
  const result: GeographicTreeNode[] = [];

  for (const node of nodes) {
    if (node.resolution === resolution) {
      result.push(node);
    }
    if (node.trees && node.trees.length > 0) {
      result.push(...getNodesByResolution(node.trees, resolution));
    }
  }

  return result;
}

/**
 * Get unique resolution levels from the tree
 */
export function getResolutionLevels(nodes: GeographicTreeNode[]): string[] {
  const levels = new Set<string>();

  function traverse(nodeList: GeographicTreeNode[]) {
    for (const node of nodeList) {
      levels.add(node.resolution);
      if (node.trees && node.trees.length > 0) {
        traverse(node.trees);
      }
    }
  }

  traverse(nodes);
  return Array.from(levels);
}

// ============================================================================
// Geographic Nodes API Types (with geometry for map display)
// ============================================================================

// Import centralized GeoJSON types
import type { GeoJSONGeometry } from "./geojson";
export type { GeoJSONGeometry };

/**
 * Geographic Node with geometry
 *
 * Represents a geographic node with its full geometry for map display.
 * Returned from /api/v1/geographic/nodes endpoint.
 */
export interface GeographicNodeWithGeometry {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  resolution: string;
  customers: number | null;
  selectable: boolean;
  geometry: GeoJSONGeometry;
  created_at: string;
  updated_at: string;
}

/**
 * API Response for geographic nodes
 */
export interface GeographicNodesResponse {
  success: boolean;
  data: GeographicNodeWithGeometry[];
  count: number;
  message: string;
  next_token: string | null;
  has_more: boolean;
  applied_filters: Record<string, any> | null;
}

/**
 * Query parameters for geographic nodes endpoint
 */
export interface GeographicNodesParams {
  resolution: string; // Resolution level (e.g., "Operating Company", "District")
  id: string; // Comma-separated list of node IDs
  geo_version?: number; // Cache-busting version from org; new value = new URL = fresh fetch
}

/**
 * Convert GeographicNodeWithGeometry array to GeoJSON FeatureCollection
 */
export function nodesToGeoJSONFeatureCollection(
  nodes: GeographicNodeWithGeometry[],
): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: nodes.map((node) => ({
      type: "Feature" as const,
      id: node.id,
      properties: {
        id: node.id,
        name: node.name,
        slug: node.slug,
        resolution: node.resolution,
        customers: node.customers,
        selectable: node.selectable,
        organization_id: node.organization_id,
      },
      geometry: node.geometry,
    })),
  };
}
